import React, { useState, useEffect } from 'react';
import './savedrecipes.css';
import './cookbook.css';

function SavedRecipes() {
  const [savedRecipes, setSavedRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingRecipeName, setEditingRecipeName] = useState({}); // Track which recipe names are being edited
  const [tempRecipeNames, setTempRecipeNames] = useState({}); // Store temporary recipe names while editing
  const [showAllRecipes, setShowAllRecipes] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('date'); // 'date' or 'alphabetical'
  const [updatingStores, setUpdatingStores] = useState({}); // Track which recipes are updating stores
  const [showShareOptions, setShowShareOptions] = useState(false); // Track whether to show all recipes or limit to 6
  
  // Cookbook states
  const [cookbookSearchQuery, setCookbookSearchQuery] = useState('');
  const [cookbookSortBy, setCookbookSortBy] = useState('date');
  const [cookbooks, setCookbooks] = useState([]);
  const [showCreateCookbook, setShowCreateCookbook] = useState(false);
  const [cookbooksLoading, setCookbooksLoading] = useState(true);
  const [showAllCookbooks, setShowAllCookbooks] = useState(false);
  const [selectedCookbook, setSelectedCookbook] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  
  // Cookbook creation states
  const [cookbookTitle, setCookbookTitle] = useState('');
  const [cookbookDescription, setCookbookDescription] = useState('');
  const [cookbookCover, setCookbookCover] = useState('mediterranean');
  const [cookbookPrice, setCookbookPrice] = useState('Free');
  const [selectedRecipesForCookbook, setSelectedRecipesForCookbook] = useState([]);
  const [cookbookPages, setCookbookPages] = useState([]);
  
  // User info for profile picture
  const [userInfo, setUserInfo] = useState({});
  
  // Get API base URL from environment variable
  const apiBaseUrl = process.env.REACT_APP_RAILWAY_URL || 'http://localhost:8000';

  // Fetch saved recipes when component mounts
  useEffect(() => {
    fetchSavedRecipes();
    fetchCookbooks();
    fetchUserInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Add a listener for localStorage changes to update profile picture
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'userInfo') {
        fetchUserInfo();
      }
    };

    // Listen for storage events (from other tabs/windows)
    window.addEventListener('storage', handleStorageChange);

    // Also listen for custom events (from same tab)
    const handleCustomUserUpdate = () => {
      fetchUserInfo();
    };
    window.addEventListener('userInfoUpdated', handleCustomUserUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userInfoUpdated', handleCustomUserUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUserInfo = async () => {
    try {
      // First check if user info is stored in localStorage (prioritize complete userInfo)
      const storedUserInfo = localStorage.getItem('userInfo');
      const storedUser = localStorage.getItem('user');
      
      // Prioritize more complete userInfo if available
      if (storedUserInfo) {
        const parsedUserInfo = JSON.parse(storedUserInfo);
        const userInfoData = {
          firstName: parsedUserInfo.firstName || parsedUserInfo.first_name || 'User',
          lastName: parsedUserInfo.lastName || parsedUserInfo.last_name || '',
          username: parsedUserInfo.username || '',
          email: parsedUserInfo.email || '',
          profilePicture: parsedUserInfo.profilePicture || parsedUserInfo.profile_picture || null
        };
        console.log('📋 SavedRecipes UserInfo loaded from localStorage:', userInfoData);
        setUserInfo(userInfoData);
        return;
      } else if (storedUser) {
        const parsedUserInfo = JSON.parse(storedUser);
        const userInfoFromLogin = {
          firstName: parsedUserInfo.firstName || parsedUserInfo.first_name || 'User',
          lastName: parsedUserInfo.lastName || parsedUserInfo.last_name || '',
          username: parsedUserInfo.username || '',
          email: parsedUserInfo.email || '',
          profilePicture: parsedUserInfo.profilePicture || parsedUserInfo.profile_picture || null
        };
        
        setUserInfo(userInfoFromLogin);
        
        // If login data includes complete profile, also store it in userInfo for consistency
        if (parsedUserInfo.profilePicture || parsedUserInfo.profile_picture) {
          localStorage.setItem('userInfo', JSON.stringify(userInfoFromLogin));
        }
        
        return;
      }

      // If not in localStorage, try to fetch from backend
      const response = await fetch(`${apiBaseUrl}/api/user-profile/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      if (response.ok) {
        const userData = await response.json();
        if (userData.success) {
          const normalizedUserInfo = {
            firstName: userData.first_name || userData.firstName || 'User',
            lastName: userData.last_name || userData.lastName || '',
            username: userData.username || '',
            email: userData.email || '',
            profilePicture: userData.profile_picture || null
          };
          
          setUserInfo(normalizedUserInfo);
          
          // Store normalized user info in localStorage for future use
          localStorage.setItem('userInfo', JSON.stringify(normalizedUserInfo));
        }
      }
    } catch (error) {
      console.error('Error fetching user info:', error);
    }
  };

  const fetchSavedRecipes = async () => {
    try {
      setLoading(true);
      
      // Get user data from localStorage
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      if (!userData.id) {
        setError('Please log in to view saved recipes');
        setLoading(false);
        return;
      }

      const response = await fetch(`${apiBaseUrl}/api/saved-recipes/?user_id=${userData.id}`);
      const data = await response.json();

      if (response.ok && data.success) {
        console.log('Loaded saved recipes:', data.recipes);
        setSavedRecipes(data.recipes);
        setError(null);
      } else {
        setError(data.error || 'Failed to load saved recipes');
      }
    } catch (error) {
      console.error('Error fetching saved recipes:', error);
      setError('Failed to load saved recipes');
    } finally {
      setLoading(false);
    }
  };

  const fetchCookbooks = async () => {
    try {
      setCookbooksLoading(true);
      
      // Get user data from localStorage
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      if (!userData.id) {
        console.log('No user logged in, skipping cookbook fetch');
        setCookbooksLoading(false);
        return;
      }

      const response = await fetch(`${apiBaseUrl}/api/cookbooks/?user_id=${userData.id}`);
      const data = await response.json();

      if (response.ok && data.success) {
        console.log('Loaded cookbooks:', data.cookbooks);
        setCookbooks(data.cookbooks);
      } else {
        console.error('Failed to load cookbooks:', data.error);
        setCookbooks([]);
      }
    } catch (error) {
      console.error('Error fetching cookbooks:', error);
      setCookbooks([]);
    } finally {
      setCookbooksLoading(false);
    }
  };

  const handleDeleteRecipe = async (recipeId, recipeName) => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      
      const response = await fetch(`${apiBaseUrl}/api/delete-recipe/${recipeId}/`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ user_id: userData.id })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Remove the deleted recipe from the list
        setSavedRecipes(prev => prev.filter(recipe => recipe.id !== recipeId));
        console.log(`Recipe "${recipeName}" deleted successfully`);
      } else {
        console.error(`Failed to delete recipe: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting recipe:', error);
    }
  };

  const handleViewCookbook = (cookbook) => {
    setSelectedCookbook(cookbook);
    setCurrentPage(0); // Start at cover page
  };

  const handleCloseCookbookViewer = () => {
    setSelectedCookbook(null);
    setCurrentPage(0);
  };

  const handleNextPage = () => {
    if (selectedCookbook) {
      const totalPages = 1 + Math.ceil(selectedCookbook.recipes.length / 2); // Cover + recipe pages (2 recipes per spread)
      if (currentPage < totalPages - 1) {
        setCurrentPage(currentPage + 1);
      }
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Function to render recipe content using exact View Full Recipe modal structure
  const renderRecipeContent = (recipeData) => {
    if (!recipeData) return null;
    
    // Handle cookbook recipe structure: { recipe: actualRecipe, pageNumber: X }
    const recipe = recipeData.recipe || recipeData;
    
    return (
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '20px',
        height: '100%',
        overflowY: 'auto',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
        position: 'relative'
      }}>
        {/* Recipe Header */}
        <div style={{ marginBottom: '18px' }}>
          <h1 style={{
            fontSize: '1.4rem',
            fontWeight: '600',
            color: '#333',
            margin: '0 0 8px 0'
          }}>
            {recipe.name}
          </h1>
          
          <div style={{
            display: 'flex',
            gap: '15px',
            alignItems: 'center',
            marginBottom: '12px'
          }}>
            {recipe.cooking_time && (
              <span style={{ color: '#666', fontSize: '0.85rem' }}>
                🕒 {recipe.cooking_time}
              </span>
            )}
            {recipe.servings && (
              <span style={{ color: '#666', fontSize: '0.85rem' }}>
                👥 {recipe.servings}
              </span>
            )}
          </div>
          
          <div className="cookbook-recipe-date-text" style={{ color: '#888' }}>
            <p>
              Saved {recipe.created_at ? new Date(recipe.created_at).toLocaleDateString() : 'Date not available'}
            </p>
            <p>
              Stores last updated: {
                recipe.updated_at && recipe.updated_at !== recipe.created_at 
                  ? new Date(recipe.updated_at).toLocaleDateString()
                  : 'Original save date'
              }
            </p>
          </div>
        </div>

        {/* Recipe Content - Single Column for cookbook pages */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
          marginBottom: '20px'
        }}>
          {/* Ingredients Section */}
          <div>
            <h3 style={{
              fontSize: '1.1rem',
              fontWeight: '600',
              color: '#333',
              marginBottom: '10px',
              borderBottom: '2px solid #0B303B',
              paddingBottom: '5px'
            }}>
              Ingredients ({filterValidIngredients(recipe.ingredients).length})
            </h3>
            <ul style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              maxHeight: '120px',
              overflowY: 'auto'
            }}>
              {(recipe.ingredients || []).map((ingredient, i) => (
                <li key={i} style={{
                  padding: '4px 0',
                  borderBottom: '1px solid #f0f0f0',
                  color: '#555',
                  fontSize: '0.8rem',
                  lineHeight: '1.3'
                }}>
                  • {ingredient}
                </li>
              ))}
            </ul>
          </div>

          {/* Instructions Section */}
          <div>
            <h3 style={{
              fontSize: '1.1rem',
              fontWeight: '600',
              color: '#333',
              marginBottom: '10px',
              borderBottom: '2px solid #0B303B',
              paddingBottom: '5px'
            }}>
              Instructions ({filterValidInstructions(recipe.instructions).length} steps)
            </h3>
            <ol style={{
              padding: '0 0 0 16px',
              margin: 0,
              maxHeight: '120px',
              overflowY: 'auto'
            }}>
              {(recipe.instructions || []).map((instruction, i) => (
                <li key={i} style={{
                  padding: '6px 0',
                  color: '#555',
                  fontSize: '0.8rem',
                  lineHeight: '1.3'
                }}>
                  {instruction}
                </li>
              ))}
            </ol>
          </div>

          {/* Stores Section */}
          <div>
            <h3 style={{
              fontSize: '1.1rem',
              fontWeight: '600',
              color: '#333',
              marginBottom: '10px',
              borderBottom: '2px solid #0B303B',
              paddingBottom: '5px'
            }}>
              Nearby Stores ({recipe.analysis_data?.stores?.length || 0})
            </h3>
            {recipe.analysis_data?.stores && recipe.analysis_data.stores.length > 0 ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                maxHeight: '100px',
                overflowY: 'auto'
              }}>
                {recipe.analysis_data.stores.map((store, i) => (
                  <div key={i} style={{
                    padding: '8px 10px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '6px',
                    border: '1px solid #e0e0e0'
                  }}>
                    <h4 style={{
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      color: '#333',
                      margin: '0 0 4px 0'
                    }}>
                      {store.name}
                    </h4>
                    <p style={{
                      color: '#666',
                      fontSize: '0.75rem',
                      margin: '0 0 3px 0'
                    }}>
                      📍 {store.distance}
                    </p>
                    {store.coverage && (
                      <p style={{
                        color: '#28a745',
                        fontSize: '0.7rem',
                        margin: 0,
                        fontWeight: '500'
                      }}>
                        {store.coverage}% ingredients available
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p style={{
                color: '#666',
                fontSize: '0.85rem',
                fontStyle: 'italic',
                margin: '8px 0',
                lineHeight: '1.4'
              }}>
                No stores found for this recipe
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const handleDeleteCookbook = async (cookbookId, cookbookTitle) => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      
      const response = await fetch(`${apiBaseUrl}/api/delete-cookbook/${cookbookId}/`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ user_id: userData.id })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Remove the deleted cookbook from the list
        setCookbooks(prev => prev.filter(cookbook => cookbook.id !== cookbookId));
        console.log(`Cookbook "${cookbookTitle}" deleted successfully`);
      } else {
        console.error(`Failed to delete cookbook: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting cookbook:', error);
    }
  };

  // Functions for editable recipe names
  const handleRecipeNameClick = (recipeId) => {
    const recipe = savedRecipes.find(r => r.id === recipeId);
    const currentName = recipe?.name || "Untitled Recipe";
    setEditingRecipeName(prev => ({...prev, [recipeId]: true}));
    setTempRecipeNames(prev => ({...prev, [recipeId]: currentName}));
  };

  const handleRecipeNameChange = (recipeId, newName) => {
    setTempRecipeNames(prev => ({...prev, [recipeId]: newName}));
  };

  const handleRecipeNameSave = async (recipeId) => {
    const newName = tempRecipeNames[recipeId] || "Untitled Recipe";
    
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      
      const response = await fetch(`${apiBaseUrl}/api/update-recipe/${recipeId}/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          user_id: userData.id,
          name: newName
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Update the recipe name in local state
        setSavedRecipes(prev => prev.map(recipe => 
          recipe.id === recipeId ? {...recipe, name: newName} : recipe
        ));
        setEditingRecipeName(prev => ({...prev, [recipeId]: false}));
        setTempRecipeNames(prev => ({...prev, [recipeId]: undefined}));
        console.log('Recipe name updated successfully');
      } else {
        console.error(`Failed to update recipe name: ${data.error || 'Unknown error'}`);
        handleRecipeNameCancel(recipeId);
      }
    } catch (error) {
      console.error('Error updating recipe name:', error);
      handleRecipeNameCancel(recipeId);
    }
  };

  const handleRecipeNameCancel = (recipeId) => {
    setEditingRecipeName(prev => ({...prev, [recipeId]: false}));
    setTempRecipeNames(prev => ({...prev, [recipeId]: undefined}));
  };

  // Handle click outside to cancel editing
  const handleRecipeNameBlur = (recipeId) => {
    // Small delay to allow save action if user clicked save button
    setTimeout(() => {
      if (editingRecipeName[recipeId]) {
        handleRecipeNameCancel(recipeId);
      }
    }, 100);
  };

  // Modal functions
  const handleViewFullRecipe = (recipe) => {
    setSelectedRecipe(recipe);
    setShowModal(true);
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedRecipe(null);
    setShowShareOptions(false);
    // Restore body scroll
    document.body.style.overflow = 'unset';
  };

  // Close modal on Escape key
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && showModal) {
        handleCloseModal();
      }
    };

    if (showModal) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [showModal]);

  // Helper functions to filter out "Not specified" entries
  const filterValidIngredients = (ingredients) => {
    return (ingredients || []).filter(ingredient => 
      ingredient && 
      ingredient.trim() !== '' && 
      !ingredient.toLowerCase().includes('not specified')
    );
  };

  const filterValidInstructions = (instructions) => {
    return (instructions || []).filter(instruction => 
      instruction && 
      instruction.trim() !== '' && 
      !instruction.toLowerCase().includes('not specified')
    );
  };

  // Filter and sort cookbooks based on search and sort criteria
  const getFilteredAndSortedCookbooks = () => {
    let filtered = cookbooks;
    
    // Apply search filter
    if (cookbookSearchQuery.trim()) {
      filtered = filtered.filter(cookbook =>
        cookbook.title.toLowerCase().includes(cookbookSearchQuery.toLowerCase()) ||
        cookbook.description.toLowerCase().includes(cookbookSearchQuery.toLowerCase()) ||
        cookbook.author.toLowerCase().includes(cookbookSearchQuery.toLowerCase())
      );
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      if (cookbookSortBy === 'alphabetical') {
        return a.title.localeCompare(b.title);
      } else {
        // Sort by date (newest first)
        return new Date(b.created_at) - new Date(a.created_at);
      }
    });
    
    return filtered;
  };

  // Get limited cookbooks (first 6) or all based on showAllCookbooks state
  const getDisplayedCookbooks = () => {
    const allCookbooks = getFilteredAndSortedCookbooks();
    return showAllCookbooks ? allCookbooks : allCookbooks.slice(0, 6);
  };

  // Filter and sort recipes based on search and sort criteria
  const getFilteredAndSortedRecipes = () => {
    let filtered = savedRecipes.filter(recipe => 
      recipe.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Sort recipes
    if (sortBy === 'alphabetical') {
      filtered.sort((a, b) => a.name.localeCompare(b.name));
    } else {
      // Default: sort by date (newest first)
      filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }

    return filtered;
  };

  // Function to update stores for a recipe
  const handleUpdateStores = async (recipe) => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const userPostalCode = userData.postal_code;
    
    if (!userPostalCode) {
      alert('Please set your postal code in your profile to update stores.');
      return;
    }

    setUpdatingStores(prev => ({...prev, [recipe.id]: true}));

    try {
      // Get fresh store data using the existing store locator API
      console.log('Fetching stores for postal code:', userPostalCode);
      console.log('Recipe ingredients:', recipe.ingredients);
      
      const response = await fetch(`${apiBaseUrl}/api/search-stores/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          searchItem: userPostalCode,
          radius: 10
        })
      });

      console.log('Store fetch response status:', response.status);
      
      if (response.ok) {
        const storeData = await response.json();
        console.log('Store data received:', storeData);
        
        // Process stores data (filter stores with coordinates and calculate coverage)
        const storesWithCoords = storeData.items ? storeData.items.filter(store => 
          store.latitude && store.longitude
        ) : [];
        
        console.log('Filtered stores with coords:', storesWithCoords);
        
        // Calculate ingredient coverage for each store (same logic as StoreLocator)
        const storesWithCoverage = storesWithCoords.map(store => {
          const ingredients = recipe.ingredients || [];
          if (ingredients.length === 0) {
            return { ...store, coverage: 0 };
          }
          
          // Simple coverage calculation - in a real app, this would check actual product availability
          // For now, we'll simulate coverage based on store type/name
          let coverage = 0;
          if (store.name && store.name.toLowerCase().includes('coop')) {
            coverage = Math.floor(Math.random() * 30) + 70; // 70-100% coverage for Coop
          } else if (store.name && (store.name.toLowerCase().includes('ica') || store.name.toLowerCase().includes('willys'))) {
            coverage = Math.floor(Math.random() * 40) + 60; // 60-100% coverage for ICA/Willys
          } else {
            coverage = Math.floor(Math.random() * 50) + 50; // 50-100% coverage for others
          }
          
          return {
            ...store,
            coverage: coverage,
            distance: store.distance || `${(Math.random() * 10 + 1).toFixed(1)} km`
          };
        });
        
        // Filter stores with reasonable coverage (>= 50%)
        const viableStores = storesWithCoverage.filter(store => store.coverage >= 50);
        
        console.log('Stores with coverage calculation:', viableStores);
        
        // Update the recipe with new store data
        const updateData = {
          user_id: userData.id,
          analysis_data: {
            ...recipe.analysis_data,
            stores: viableStores
          }
        };
        
        console.log('Updating recipe with data:', updateData);
        
        const updateResponse = await fetch(`${apiBaseUrl}/api/update-recipe/${recipe.id}/`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updateData)
        });

        console.log('Update response status:', updateResponse.status);

        if (updateResponse.ok) {
          // Refresh the saved recipes list
          fetchSavedRecipes();
          alert(`Stores updated! Found ${viableStores.length} stores with good ingredient coverage (${storesWithCoords.length} total stores checked).`);
        } else {
          const errorText = await updateResponse.text();
          console.error('Update response error:', errorText);
          throw new Error(`Failed to update recipe stores: ${errorText}`);
        }
      } else {
        const errorText = await response.text();
        console.error('Store fetch error:', errorText);
        throw new Error(`Failed to fetch updated store data: ${errorText}`);
      }
    } catch (error) {
      console.error('Error updating stores:', error);
      alert(`Failed to update stores: ${error.message}. Please check the console for details.`);
    } finally {
      setUpdatingStores(prev => ({...prev, [recipe.id]: false}));
    }
  };

  // Share recipe functions
  const generateShareText = (recipe) => {
    // Generate ingredients list (same format as stores)
    const ingredientsList = recipe.ingredients?.length > 0 
      ? recipe.ingredients.map(ingredient => `- ${ingredient}`).join('\n')
      : 'No ingredients listed';
    
    // Generate store list
    const storesList = recipe.analysis_data?.stores?.length > 0 
      ? recipe.analysis_data.stores.map(store => `- ${store.name} (${store.distance})`).join('\n')
      : 'No stores found';
    
    return `🍽️ ${recipe.name}
🕒 ${recipe.cooking_time || 'Time not specified'} | 👥 ${recipe.servings || 'Servings not specified'}
Ingredients:
${ingredientsList}
Nearby Stores with ingredients on deal:
${storesList}

Shared from Penny - Your smart recipe assistant!`;
  };

  const handleShare = (method) => {
    const shareText = generateShareText(selectedRecipe);
    const encodedText = encodeURIComponent(shareText);
    
    switch (method) {
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodedText}`, '_blank');
        break;
      case 'messenger':
        // Messenger sharing requires Facebook App ID, so we'll use a generic share
        if (navigator.share) {
          navigator.share({
            title: selectedRecipe.name,
            text: shareText
          });
        } else {
          // Fallback to copying to clipboard
          navigator.clipboard.writeText(shareText);
          alert('Recipe copied to clipboard! You can paste it in Messenger.');
        }
        break;
      case 'sms':
        window.open(`sms:?body=${encodedText}`, '_blank');
        break;
      case 'email':
        const subject = encodeURIComponent(`Recipe: ${selectedRecipe.name}`);
        window.open(`mailto:?subject=${subject}&body=${encodedText}`, '_blank');
        break;
      case 'copy':
        navigator.clipboard.writeText(shareText);
        alert('Recipe copied to clipboard!');
        break;
      case 'notes':
        // Add to Notes app (iOS/macOS) or copy to clipboard with instructions
        if (navigator.userAgent.includes('Mac') || navigator.userAgent.includes('iPhone') || navigator.userAgent.includes('iPad')) {
          window.open(`notes://new?body=${encodedText}`, '_blank');
        } else {
          navigator.clipboard.writeText(shareText);
          alert('Recipe copied to clipboard! You can now paste it in your Notes app.');
        }
        break;
      default:
        if (navigator.share) {
          navigator.share({
            title: selectedRecipe.name,
            text: shareText
          });
        }
    }
    setShowShareOptions(false);
  };



  // Cookbook creation functions
  const handleCreateCookbook = () => {
    setShowCreateCookbook(true);
    // Reset form
    setCookbookTitle('');
    setCookbookDescription('');
    setCookbookCover('mediterranean');
    setCookbookPrice('Free');
    setSelectedRecipesForCookbook([]);
    setCookbookPages([]);
  };

  const handleCloseCookbookCreator = () => {
    setShowCreateCookbook(false);
  };

  const handleAddRecipeToCookbook = (recipe) => {
    if (!selectedRecipesForCookbook.find(r => r.id === recipe.id)) {
      setSelectedRecipesForCookbook([...selectedRecipesForCookbook, recipe]);
      setCookbookPages([...cookbookPages, { recipe, pageNumber: cookbookPages.length + 1 }]);
    }
  };

  const handleRemoveRecipeFromCookbook = (recipeId) => {
    setSelectedRecipesForCookbook(selectedRecipesForCookbook.filter(r => r.id !== recipeId));
    const newPages = cookbookPages.filter(p => p.recipe.id !== recipeId);
    // Reorder page numbers
    setCookbookPages(newPages.map((page, index) => ({ ...page, pageNumber: index + 1 })));
  };

  const handleSaveCookbook = async () => {
    if (!cookbookTitle.trim()) {
      alert('Please enter a cookbook title');
      return;
    }

    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      if (!userData.id) {
        alert('Please log in to save cookbooks');
        return;
      }

      const cookbookData = {
        user_id: userData.id,
        title: cookbookTitle,
        description: cookbookDescription || `A collection of ${selectedRecipesForCookbook.length} delicious recipes`,
        cover_image: cookbookCover,
        price: cookbookPrice,
        rating: 5.0,
        recipes: cookbookPages,
        is_published: false
      };

      console.log('Saving cookbook:', cookbookData);
      
      // Log the size of the request for debugging
      const requestBody = JSON.stringify(cookbookData);
      console.log(`🔵 Request body size: ${requestBody.length} characters`);
      if (cookbookData.cover_image && cookbookData.cover_image.startsWith('custom:')) {
        console.log(`🔵 Custom image size: ${cookbookData.cover_image.length} characters`);
      }

      const response = await fetch(`${apiBaseUrl}/api/save-cookbook/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: requestBody
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Create the cookbook object for frontend display
        const newCookbook = {
          id: data.cookbook.id,
          title: data.cookbook.title,
          description: data.cookbook.description,
          cover_image: data.cookbook.cover_image,
          recipes_count: data.cookbook.recipes_count,
          price: data.cookbook.price,
          rating: data.cookbook.rating,
          created_at: data.cookbook.created_at,
          author: userData.email || "You",
          recipes: cookbookPages
        };

        setCookbooks([newCookbook, ...cookbooks]);
        setShowCreateCookbook(false);
        
        console.log('Cookbook saved successfully:', data.cookbook);
        alert('Cookbook created successfully!');
      } else {
        console.error('Failed to save cookbook:', data.error);
        alert(`Failed to save cookbook: ${data.error}`);
      }
    } catch (error) {
      console.error('Error saving cookbook:', error);
      alert('Failed to save cookbook. Please try again.');
    }
  };

  const coverOptions = [
    { value: 'mediterranean', label: 'Mediterranean', gradient: 'linear-gradient(to right, #2c3e50, #4ca1af)' },
    { value: 'weeknight', label: 'Weeknight', gradient: 'linear-gradient(to right, #ff9966, #ff5e62)' },
    { value: 'budget', label: 'Budget', gradient: 'linear-gradient(to right, #56ab2f, #a8e063)' },
    { value: 'elegant', label: 'Elegant', gradient: 'linear-gradient(to right, #bdc3c7, #2c3e50)' },
    { value: 'rustic', label: 'Rustic', gradient: 'linear-gradient(to right, #d4a574, #8b4513)' },
    { value: 'modern', label: 'Modern', gradient: 'linear-gradient(to right, #f5f7fa, #c3cfe2)' },
    { value: 'custom', label: 'Upload Custom', gradient: null }
  ];

  // Handle custom cover image upload
  const handleCoverImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Check if it's an image
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file (PNG, JPG, etc.)');
        return;
      }

      // Check file size (max 2MB for better performance)
      if (file.size > 2 * 1024 * 1024) {
        alert('Image size must be less than 2MB');
        return;
      }

      // Create a canvas to resize/compress the image
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Set canvas dimensions (max 400x400 for cookbook covers)
        const maxSize = 400;
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress the image
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to base64 with compression (0.6 quality for smaller size)
        const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.6);
        
        // Check if the compressed image is still too large (over 100KB base64)
        if (compressedDataUrl.length > 100000) {
          // Try even more aggressive compression
          const veryCompressedDataUrl = canvas.toDataURL('image/jpeg', 0.4);
          console.log('🟡 Using very aggressive compression for large image');
          setCookbookCover(`custom:${veryCompressedDataUrl}`);
        } else {
          setCookbookCover(`custom:${compressedDataUrl}`);
        }
      };
      
      // Load the image
      const reader = new FileReader();
      reader.onload = (e) => {
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div>
      {/* Saved Recipes Module */}
    <div className="savedrecipes-container">
      <div className="savedrecipes-header">
          <div className="header-title-section">
        <h1>Saved Recipes</h1>
            <div className="header-stats">
              <span className="header-stat">{loading ? '...' : getFilteredAndSortedRecipes().length} recipes</span>
            </div>
          </div>
        <p className="savedrecipes-subtitle">Access and organize all your saved recipes from Penny and around the web</p>
          
          {/* Search and Filter Bar */}
          <div className="savedrecipes-search-filter-bar">
            <div className="savedrecipes-search-container">
              <input
                type="text"
                placeholder="Search recipes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="savedrecipes-search-input"
              />
            </div>
            
            <div className="savedrecipes-filter-container">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="savedrecipes-filter-select"
              >
                <option value="date">Sort by Date</option>
                <option value="alphabetical">Sort A-Z</option>
              </select>
            </div>
          </div>
        </div>

        {/* Display saved recipes directly */}
        {error && (
          <div className="error-message" style={{ 
            color: 'red', 
            padding: '10px', 
            backgroundColor: '#ffe6e6', 
            borderRadius: '4px', 
            margin: '10px 0' 
          }}>
            {error}
          </div>
        )}
        
        {loading ? (
          <div className="saved-recipes-grid">
            {[...Array(6)].map((_, index) => (
              <div key={index} className="skeleton-recipe-card">
                <div className="skeleton-recipe-header skeleton"></div>
                <div className="skeleton-recipe-meta">
                  <div className="skeleton-recipe-time skeleton"></div>
                  <div className="skeleton-recipe-servings skeleton"></div>
                </div>
                <div className="skeleton-recipe-content">
                  <div className="skeleton-recipe-section">
                    <div className="skeleton-recipe-title skeleton"></div>
                    <div className="skeleton-recipe-list">
                      <div className="skeleton-recipe-item skeleton"></div>
                      <div className="skeleton-recipe-item skeleton"></div>
                      <div className="skeleton-recipe-item skeleton"></div>
                    </div>
                  </div>
                  <div className="skeleton-recipe-section">
                    <div className="skeleton-recipe-title skeleton"></div>
                    <div className="skeleton-recipe-list">
                      <div className="skeleton-recipe-item skeleton"></div>
                      <div className="skeleton-recipe-item skeleton"></div>
                    </div>
                  </div>
                  <div className="skeleton-recipe-section">
                    <div className="skeleton-recipe-title skeleton"></div>
                    <div className="skeleton-recipe-list">
                      <div className="skeleton-recipe-item skeleton"></div>
                    </div>
                  </div>
                </div>
                <div className="skeleton-recipe-footer">
                  <div className="skeleton-recipe-button skeleton"></div>
                  <div className="skeleton-recipe-button skeleton"></div>
                </div>
              </div>
            ))}
          </div>
        ) : getFilteredAndSortedRecipes().length === 0 ? (
          <div className="no-recipes-message">
            <p>{searchQuery ? `No recipes found matching "${searchQuery}"` : 'No recipes saved yet, start saving recipes using the Recipe Engine'}</p>
          </div>
        ) : (
          <>
            <div className="saved-recipes-grid">
              {(showAllRecipes ? getFilteredAndSortedRecipes() : getFilteredAndSortedRecipes().slice(0, 6)).map((recipe) => (
              <div key={recipe.id} className="saved-recipe-card">
                <div className="saved-recipe-header">
                  {editingRecipeName[recipe.id] ? (
                    <input
                      type="text"
                      value={tempRecipeNames[recipe.id] || ''}
                      onChange={(e) => handleRecipeNameChange(recipe.id, e.target.value)}
                      style={{
                        fontSize: '1.2rem',
                        fontWeight: '600',
                        color: '#333',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        padding: '5px 10px',
                        background: 'white',
                        width: '100%',
                        outline: 'none'
                      }}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleRecipeNameSave(recipe.id);
                        if (e.key === 'Escape') handleRecipeNameCancel(recipe.id);
                      }}
                      onBlur={() => handleRecipeNameBlur(recipe.id)}
                    />
                  ) : (
                    <h3 
                      className="saved-recipe-title"
                      style={{ cursor: 'pointer', padding: '5px', borderRadius: '4px', transition: 'background-color 0.2s ease' }}
                      onClick={() => handleRecipeNameClick(recipe.id)}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      title="Click to edit recipe name"
                    >
                      {recipe.name}
                    </h3>
                  )}
                  <button 
                    className="delete-recipe-button"
                    onClick={() => handleDeleteRecipe(recipe.id, recipe.name)}
                    title="Delete Recipe"
                  >
                    ×
                  </button>
                </div>
                
                <div className="saved-recipe-meta">
                  {recipe.cooking_time && (
                    <span className="recipe-meta-item">🕒 {recipe.cooking_time}</span>
                  )}
                  {recipe.servings && (
                    <span className="recipe-meta-item">👥 {recipe.servings}</span>
                  )}
                </div>
                
                <div className="saved-recipe-content">
                  <div className="recipe-section">
                    <h4>Ingredients ({filterValidIngredients(recipe.ingredients).length})</h4>
                    <ul className="ingredients-preview">
                      {filterValidIngredients(recipe.ingredients).slice(0, 3).map((ingredient, i) => (
                        <li key={i}>{ingredient}</li>
                      ))}
                      {filterValidIngredients(recipe.ingredients).length > 3 && (
                        <li>... and {filterValidIngredients(recipe.ingredients).length - 3} more</li>
                      )}
                    </ul>
                  </div>
                  
                  <div className="recipe-section">
                    <h4>Instructions ({filterValidInstructions(recipe.instructions).length} steps)</h4>
                    <div className="instructions-preview">
                      {filterValidInstructions(recipe.instructions)[0] && (
                        <p>{filterValidInstructions(recipe.instructions)[0].substring(0, 100)}...</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="recipe-section">
                    <h4>Nearby Stores ({recipe.analysis_data?.stores?.length || 0})</h4>
                    <div className="stores-preview">
                      {recipe.analysis_data?.stores && recipe.analysis_data.stores.length > 0 && (
                        <>
                          {recipe.analysis_data.stores.slice(0, 2).map((store, i) => (
                            <p key={i} className="store-item">
                              <strong>{store.name}</strong> - {store.distance}
                            </p>
                          ))}
                          {recipe.analysis_data.stores.length > 2 && (
                            <p className="store-item">... and {recipe.analysis_data.stores.length - 2} more stores</p>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="saved-recipe-footer">
                  <span className="recipe-date">
                    Saved {new Date(recipe.created_at).toLocaleDateString()}
                  </span>
                  <div className="recipe-buttons">
                    <button 
                      className="update-stores-button"
                      onClick={() => handleUpdateStores(recipe)}
                      disabled={updatingStores[recipe.id]}
                      title="Update stores with current campaigns"
                    >
                      {updatingStores[recipe.id] ? 'Updating...' : 'Update Stores'}
                    </button>
                    <button 
                      className="view-full-recipe-button"
                      onClick={() => handleViewFullRecipe(recipe)}
                    >
                      View Full Recipe
                    </button>
                  </div>
                </div>
              </div>
              ))}
            </div>
            
            {/* Show All / Show Less Button */}
            {getFilteredAndSortedRecipes().length > 6 && (
              <div style={{ textAlign: 'center', marginTop: '30px' }}>
                <button
                  onClick={() => setShowAllRecipes(!showAllRecipes)}
                  className="show-all-button"
                >
                  {showAllRecipes ? 'Show Less' : `Show All Results (${getFilteredAndSortedRecipes().length})`}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Full Recipe Modal */}
      {showModal && selectedRecipe && (
        <div 
          className="recipe-modal-overlay"
          onClick={handleCloseModal}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px',
            animation: 'modalFadeIn 0.3s ease-out'
          }}
        >
          <div 
            className="recipe-modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '30px',
              maxWidth: '800px',
              maxHeight: '90vh',
              width: '100%',
              overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              position: 'relative',
              animation: 'modalSlideIn 0.4s ease-out'
            }}
          >
            {/* Close Button */}
            <button
              onClick={handleCloseModal}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#666',
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f0f0f0';
                e.target.style.color = '#333';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#666';
              }}
              title="Close"
            >
              ×
            </button>

            {/* Recipe Header */}
            <div style={{ marginBottom: '25px' }}>
              <h1 style={{
                fontSize: '2rem',
                fontWeight: '600',
                color: '#333',
                margin: '0 0 10px 0'
              }}>
                {selectedRecipe.name}
              </h1>
              
              <div style={{
                display: 'flex',
                gap: '20px',
                alignItems: 'center',
                marginBottom: '15px'
              }}>
                {selectedRecipe.cooking_time && (
                  <span style={{ color: '#666', fontSize: '1rem' }}>
                    🕒 {selectedRecipe.cooking_time}
                  </span>
                )}
                {selectedRecipe.servings && (
                  <span style={{ color: '#666', fontSize: '1rem' }}>
                    👥 {selectedRecipe.servings}
                  </span>
                )}
              </div>
              
              <div style={{ color: '#888', fontSize: '0.9rem', margin: 0 }}>
                <p style={{ margin: '0 0 5px 0' }}>
                  Saved {new Date(selectedRecipe.created_at).toLocaleDateString()}
                </p>
                <p style={{ margin: 0 }}>
                  Stores last updated: {
                    selectedRecipe.updated_at && selectedRecipe.updated_at !== selectedRecipe.created_at 
                      ? new Date(selectedRecipe.updated_at).toLocaleDateString()
                      : 'Original save date'
                  }
                </p>
              </div>
            </div>

            {/* Recipe Content Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '30px',
              marginBottom: '30px'
            }}>
              {/* Ingredients Section */}
              <div>
                <h3 style={{
                  fontSize: '1.3rem',
                  fontWeight: '600',
                  color: '#333',
                  marginBottom: '15px',
                  borderBottom: '2px solid #0B303B',
                  paddingBottom: '8px'
                }}>
                  Ingredients ({filterValidIngredients(selectedRecipe.ingredients).length})
                </h3>
                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0
                }}>
                  {(selectedRecipe.ingredients || []).map((ingredient, i) => (
                    <li key={i} style={{
                      padding: '8px 0',
                      borderBottom: '1px solid #f0f0f0',
                      color: '#555',
                      fontSize: '0.95rem',
                      lineHeight: '1.4'
                    }}>
                      • {ingredient}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Instructions Section */}
              <div>
                <h3 style={{
                  fontSize: '1.3rem',
                  fontWeight: '600',
                  color: '#333',
                  marginBottom: '15px',
                  borderBottom: '2px solid #0B303B',
                  paddingBottom: '8px'
                }}>
                  Instructions ({filterValidInstructions(selectedRecipe.instructions).length} steps)
                </h3>
                <ol style={{
                  padding: '0 0 0 20px',
                  margin: 0
                }}>
                  {(selectedRecipe.instructions || []).map((instruction, i) => (
                    <li key={i} style={{
                      padding: '10px 0',
                      color: '#555',
                      fontSize: '0.95rem',
                      lineHeight: '1.5'
                    }}>
                      {instruction}
                    </li>
                  ))}
                </ol>
              </div>
            </div>

            {/* Stores Section */}
            <div>
              <h3 style={{
                fontSize: '1.3rem',
                fontWeight: '600',
                color: '#333',
                marginBottom: '15px',
                borderBottom: '2px solid #0B303B',
                paddingBottom: '8px'
              }}>
                Nearby Stores ({selectedRecipe.analysis_data?.stores?.length || 0})
              </h3>
              {selectedRecipe.analysis_data?.stores && selectedRecipe.analysis_data.stores.length > 0 ? (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                  gap: '15px'
                }}>
                  {selectedRecipe.analysis_data.stores.map((store, i) => (
                    <div key={i} style={{
                      padding: '15px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '8px',
                      border: '1px solid #e0e0e0'
                    }}>
                      <h4 style={{
                        fontSize: '1rem',
                        fontWeight: '600',
                        color: '#333',
                        margin: '0 0 8px 0'
                      }}>
                        {store.name}
                      </h4>
                      <p style={{
                        color: '#666',
                        fontSize: '0.9rem',
                        margin: '0 0 5px 0'
                      }}>
                        📍 {store.distance}
                      </p>
                      {store.coverage && (
                        <p style={{
                          color: '#28a745',
                          fontSize: '0.85rem',
                          margin: 0,
                          fontWeight: '500'
                        }}>
                          {store.coverage}% ingredients available
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{
                  color: '#666',
                  fontSize: '1rem',
                  fontStyle: 'italic',
                  margin: '10px 0',
                  lineHeight: '1.5'
                }}>
                  No stores found for this recipe
                </p>
              )}
            </div>

            {/* Share Button at Bottom */}
            <div style={{ 
              textAlign: 'center', 
              marginTop: '30px',
              paddingTop: '20px',
              borderTop: '1px solid #f0f0f0',
              position: 'relative'
            }}>
              <button
                onClick={() => setShowShareOptions(!showShareOptions)}
                style={{
                  backgroundColor: 'transparent',
                  border: '2px solid #0B303B',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  cursor: 'pointer',
                  color: '#0B303B',
                  fontSize: '1rem',
                  fontWeight: '500',
                  transition: 'all 0.2s ease'
                }}
              >
                Share Recipe
              </button>
              
              {/* Share Options Dropdown */}
              {showShareOptions && (
                <div style={{
                  position: 'absolute',
                  bottom: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  backgroundColor: 'white',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  zIndex: 1000,
                  minWidth: '200px',
                  marginBottom: '10px'
                }}>
                  <button
                    onClick={() => handleShare('whatsapp')}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      border: 'none',
                      background: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      borderBottom: '1px solid #f0f0f0'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    WhatsApp
                  </button>
                  <button
                    onClick={() => handleShare('sms')}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      border: 'none',
                      background: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      borderBottom: '1px solid #f0f0f0'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    Text Message
                  </button>
                  <button
                    onClick={() => handleShare('messenger')}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      border: 'none',
                      background: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      borderBottom: '1px solid #f0f0f0'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    Messenger
                  </button>
                  <button
                    onClick={() => handleShare('email')}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      border: 'none',
                      background: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      borderBottom: '1px solid #f0f0f0'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    Email
                  </button>
                  <button
                    onClick={() => handleShare('notes')}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      border: 'none',
                      background: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      borderBottom: '1px solid #f0f0f0'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    Add to Notes
                  </button>
                  <button
                    onClick={() => handleShare('copy')}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      border: 'none',
                      background: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '0.9rem'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    Copy to Clipboard
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Your Cookbooks Module */}
      <div className="cookbook-container">
        {/* Header with stats and create button */}
        <div className="cookbook-header-new">
          <div className="cookbook-title-section">
            <h1>Your Cookbooks</h1>
            <span className="cookbook-stats">{getFilteredAndSortedCookbooks().length} cookbooks</span>
          </div>
          
          {/* Create Cookbook Button */}
          <div 
            className="create-cookbook-button"
            onClick={handleCreateCookbook}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '60px',
              height: '60px',
              border: '2px solid #0B303B',
              borderRadius: '12px',
              backgroundColor: 'transparent',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              fontSize: '24px',
              color: '#0B303B',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#f8f9fa';
              e.target.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.transform = 'translateY(0)';
            }}
          >
            +
          </div>
        </div>

        <p className="cookbook-subtitle">Create, organize, and share your personalized cookbook collections</p>

        {/* Search and Filter Bar */}
        <div className="cookbook-search-filter-bar">
          <div className="cookbook-search-container">
            <input
              type="text"
              placeholder="Search cookbooks by name, description, or author..."
              className="cookbook-search-input"
              value={cookbookSearchQuery}
              onChange={(e) => setCookbookSearchQuery(e.target.value)}
            />
          </div>
          
          <div className="cookbook-filter-container">
            <select 
              className="cookbook-filter-select"
              value={cookbookSortBy}
              onChange={(e) => setCookbookSortBy(e.target.value)}
            >
              <option value="date">Sort by Date</option>
              <option value="alphabetical">Sort A-Z</option>
            </select>
          </div>
        </div>

        {/* Cookbooks Grid */}
        <div className="cookbooks-grid">
          {cookbooksLoading ? (
            [...Array(3)].map((_, index) => (
              <div key={index} className="skeleton-cookbook-card">
                <div className="skeleton-cookbook-image skeleton"></div>
                <div className="skeleton-cookbook-content">
                  <div className="skeleton-cookbook-avatar skeleton"></div>
                  <div className="skeleton-cookbook-title skeleton"></div>
                  <div className="skeleton-cookbook-description skeleton"></div>
                  <div className="skeleton-cookbook-description skeleton"></div>
                  <div className="skeleton-cookbook-meta">
                    <div className="skeleton-cookbook-stat skeleton"></div>
                    <div className="skeleton-cookbook-stat skeleton"></div>
                    <div className="skeleton-cookbook-stat skeleton"></div>
                  </div>
                  <div className="skeleton-cookbook-author skeleton"></div>
                </div>
              </div>
            ))
          ) : getFilteredAndSortedCookbooks().length === 0 ? (
            <div className="no-cookbooks-placeholder" style={{ width: '100%', gridColumn: '1 / -1' }}>
              <p style={{ fontStyle: 'italic', color: '#666', textAlign: 'center', margin: '40px 0', whiteSpace: 'nowrap' }}>
                No cookbooks yet, start creating your first cookbook collection
              </p>
            </div>
          ) : (
            getDisplayedCookbooks().map(cookbook => (
              <div key={cookbook.id} className="cookbook-card" onClick={() => handleViewCookbook(cookbook)}>
                <div className="cookbook-image">
                  {cookbook.cover_image && cookbook.cover_image.startsWith('custom:') ? (
                    <div 
                      className="cookbook-placeholder custom"
                      style={{
                        backgroundImage: `url(${cookbook.cover_image.split('custom:')[1]})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                      }}
                    ></div>
                  ) : (
                    <div className={`cookbook-placeholder ${cookbook.cover_image}`}></div>
                  )}
                  <button 
                    className="delete-cookbook-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteCookbook(cookbook.id, cookbook.title);
                    }}
                    title="Delete Cookbook"
                  >
                    ×
                  </button>
                </div>
                <div className="cookbook-info">
                  <div className="cookbook-header">
                    <div className="cookbook-avatar">
                      {userInfo.profilePicture ? (
                        <img 
                          src={userInfo.profilePicture} 
                          alt="Author" 
                          style={{
                            width: '30px',
                            height: '30px',
                            borderRadius: '50%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <div className="small-avatar-placeholder">
                          {userInfo.firstName ? userInfo.firstName.charAt(0).toUpperCase() : (cookbook.title ? cookbook.title.charAt(0) : 'U')}
                        </div>
                      )}
                    </div>
                    <div className="cookbook-author">
                      {userInfo.username || userInfo.firstName || 'User'}
                    </div>
                  </div>
                  <h3 className="cookbook-title">{cookbook.title}</h3>
                  <p className="cookbook-description">{cookbook.description}</p>
                  <div className="cookbook-meta">
                    <div className="cookbook-recipes">{cookbook.recipes_count} recipes</div>
                    <div className="cookbook-rating">★ {cookbook.rating.toFixed(1)}</div>
                    <div className="cookbook-type">
                      {cookbook.price === 'Free' || cookbook.price === '0 SEK' || cookbook.price === '0.00 SEK' ? 'Free' : cookbook.price}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Show All Cookbooks Button */}
        {!showAllCookbooks && getFilteredAndSortedCookbooks().length > 6 && (
          <div style={{ textAlign: 'center', marginTop: '30px' }}>
            <button 
              className="show-all-button"
              onClick={() => setShowAllCookbooks(true)}
            >
              Show All Cookbooks
            </button>
          </div>
        )}
      </div>

      {/* Cookbook Creation Modal */}
      {showCreateCookbook && (
        <div 
          className="cookbook-creator-overlay"
          onClick={handleCloseCookbookCreator}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px',
            animation: 'modalFadeIn 0.3s ease-out'
          }}
        >
          <div 
            className="cookbook-creator-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '30px',
              maxWidth: '1200px',
              maxHeight: '90vh',
              width: '100%',
              overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              position: 'relative',
              animation: 'modalSlideIn 0.4s ease-out'
            }}
          >
            {/* Close Button */}
            <button
              onClick={handleCloseCookbookCreator}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#666',
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f0f0f0';
                e.target.style.color = '#333';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#666';
              }}
              title="Close"
            >
              ×
            </button>

            {/* Header */}
            <div style={{ marginBottom: '30px' }}>
              <h1 style={{
                fontSize: '2.5rem',
                fontWeight: '600',
                color: '#0B303B',
                margin: '0 0 10px 0'
              }}>
                Create Your Cookbook
              </h1>
              <p style={{
                fontSize: '1.1rem',
                color: '#666',
                margin: 0
              }}>
                Design a personalized cookbook with your saved recipes
              </p>
            </div>

            {/* Cookbook Form */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 2fr',
              gap: '40px',
              marginBottom: '30px'
            }}>
              {/* Left Column - Cookbook Details */}
              <div>
                <h3 style={{
                  fontSize: '1.3rem',
                  fontWeight: '600',
                  color: '#333',
                  marginBottom: '20px'
                }}>
                  Cookbook Details
                </h3>
                
                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '1rem',
                    fontWeight: '500',
                    color: '#333',
                    marginBottom: '8px'
                  }}>
                    Title *
                  </label>
                  <input
                    type="text"
                    value={cookbookTitle}
                    onChange={(e) => setCookbookTitle(e.target.value)}
                    placeholder="e.g., My Family Recipes"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e0e0e0',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.2s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#0B303B'}
                    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '1rem',
                    fontWeight: '500',
                    color: '#333',
                    marginBottom: '8px'
                  }}>
                    Description
                  </label>
                  <textarea
                    value={cookbookDescription}
                    onChange={(e) => setCookbookDescription(e.target.value)}
                    placeholder="A brief description of your cookbook..."
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e0e0e0',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.2s',
                      resize: 'vertical'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#0B303B'}
                    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '1rem',
                    fontWeight: '500',
                    color: '#333',
                    marginBottom: '8px'
                  }}>
                    Cover Design
                  </label>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '10px'
                  }}>
                    {coverOptions.map(option => (
                      <div
                        key={option.value}
                        onClick={() => {
                          if (option.value === 'custom') {
                            // Trigger file input
                            document.getElementById('cover-upload-input').click();
                          } else {
                            setCookbookCover(option.value);
                          }
                        }}
                        style={{
                          padding: '15px',
                          border: (cookbookCover === option.value || (option.value === 'custom' && cookbookCover.startsWith('custom:'))) ? '3px solid #0B303B' : '2px solid #e0e0e0',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          textAlign: 'center'
                        }}
                      >
                        <div
                          style={{
                            width: '100%',
                            height: '60px',
                            background: option.value === 'custom' ? 
                              (cookbookCover.startsWith('custom:') ? 
                                `url(${cookbookCover.split('custom:')[1]}) center/cover` : 
                                '#f0f0f0'
                              ) : 
                              option.gradient,
                            borderRadius: '4px',
                            marginBottom: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#999',
                            fontSize: '24px'
                          }}
                        >
                          {option.value === 'custom' && !cookbookCover.startsWith('custom:') && '+'}
                        </div>
                        <span style={{
                          fontSize: '0.9rem',
                          fontWeight: '500',
                          color: (cookbookCover === option.value || (option.value === 'custom' && cookbookCover.startsWith('custom:'))) ? '#0B303B' : '#666'
                        }}>
                          {option.label}
                        </span>
                      </div>
                    ))}
                    
                    {/* Hidden file input for custom cover upload */}
                    <input
                      id="cover-upload-input"
                      type="file"
                      accept="image/*"
                      onChange={handleCoverImageUpload}
                      style={{ display: 'none' }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '1rem',
                    fontWeight: '500',
                    color: '#333',
                    marginBottom: '8px'
                  }}>
                    Price
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      value={cookbookPrice === 'Free' ? '' : cookbookPrice.replace(' SEK', '')}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || value === '0' || value === '0.0' || value === '0.00') {
                          setCookbookPrice('Free');
                        } else {
                          // Only allow numbers and decimal point
                          const numericValue = value.replace(/[^0-9.]/g, '');
                          if (numericValue && !isNaN(numericValue) && parseFloat(numericValue) > 0) {
                            setCookbookPrice(`${numericValue} SEK`);
                          } else if (numericValue === '' || parseFloat(numericValue) === 0) {
                            setCookbookPrice('Free');
                          }
                        }
                      }}
                      placeholder="Enter price (leave empty for free)"
                      style={{
                        width: '100%',
                        padding: '12px 60px 12px 16px',
                        border: '2px solid #e0e0e0',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none'
                      }}
                    />
                    <span style={{
                      position: 'absolute',
                      right: '16px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#666',
                      fontSize: '1rem',
                      fontWeight: '500',
                      pointerEvents: 'none'
                    }}>
                      {cookbookPrice === 'Free' ? 'FREE' : 'SEK'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Right Column - Recipe Selection */}
              <div>
                <h3 style={{
                  fontSize: '1.3rem',
                  fontWeight: '600',
                  color: '#333',
                  marginBottom: '20px'
                }}>
                  Add Recipes ({selectedRecipesForCookbook.length} selected)
                </h3>
                
                {/* Available Recipes */}
                <div style={{
                  maxHeight: '400px',
                  overflowY: 'auto',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  padding: '15px'
                }}>
                  {savedRecipes.length === 0 ? (
                    <p style={{
                      color: '#666',
                      textAlign: 'center',
                      fontStyle: 'italic',
                      margin: '20px 0'
                    }}>
                      No saved recipes available. Save some recipes first!
                    </p>
                  ) : (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px'
                    }}>
                      {savedRecipes.map(recipe => (
                        <div
                          key={recipe.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '12px',
                            border: selectedRecipesForCookbook.find(r => r.id === recipe.id) 
                              ? '2px solid #0B303B' 
                              : '1px solid #f0f0f0',
                            borderRadius: '8px',
                            backgroundColor: selectedRecipesForCookbook.find(r => r.id === recipe.id)
                              ? '#f8f9ff'
                              : 'white',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <div>
                            <h4 style={{
                              margin: '0 0 4px 0',
                              fontSize: '1rem',
                              fontWeight: '600',
                              color: '#333'
                            }}>
                              {recipe.name}
                            </h4>
                            <p style={{
                              margin: 0,
                              fontSize: '0.85rem',
                              color: '#666'
                            }}>
                              {filterValidIngredients(recipe.ingredients).length} ingredients • {filterValidInstructions(recipe.instructions).length} steps
                            </p>
                          </div>
                          <button
                            onClick={() => selectedRecipesForCookbook.find(r => r.id === recipe.id)
                              ? handleRemoveRecipeFromCookbook(recipe.id)
                              : handleAddRecipeToCookbook(recipe)
                            }
                            style={{
                              padding: '6px 12px',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '0.9rem',
                              cursor: 'pointer',
                              fontWeight: '500',
                              backgroundColor: selectedRecipesForCookbook.find(r => r.id === recipe.id)
                                ? '#dc3545'
                                : '#0B303B',
                              color: 'white',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            {selectedRecipesForCookbook.find(r => r.id === recipe.id) ? 'Remove' : 'Add'}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Selected Recipes Preview */}
                {selectedRecipesForCookbook.length > 0 && (
                  <div style={{ marginTop: '20px' }}>
                    <h4 style={{
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      color: '#333',
                      marginBottom: '15px'
                    }}>
                      Recipe Order (Drag to reorder)
                    </h4>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      maxHeight: '200px',
                      overflowY: 'auto'
                    }}>
                      {cookbookPages.map((page, index) => (
                        <div
                          key={page.recipe.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '8px 12px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '6px',
                            fontSize: '0.9rem'
                          }}
                        >
                          <span style={{
                            fontWeight: '600',
                            color: '#0B303B',
                            marginRight: '10px',
                            minWidth: '30px'
                          }}>
                            {page.pageNumber}.
                          </span>
                          <span style={{ flex: 1 }}>
                            {page.recipe.name}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingTop: '20px',
              borderTop: '1px solid #f0f0f0'
            }}>
              <button
                onClick={handleCloseCookbookCreator}
                style={{
                  padding: '12px 24px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  backgroundColor: 'white',
                  color: '#666',
                  fontWeight: '500',
                  transition: 'all 0.2s ease'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCookbook}
                disabled={!cookbookTitle.trim() || selectedRecipesForCookbook.length === 0}
                style={{
                  padding: '12px 30px',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: cookbookTitle.trim() && selectedRecipesForCookbook.length > 0 ? 'pointer' : 'not-allowed',
                  backgroundColor: cookbookTitle.trim() && selectedRecipesForCookbook.length > 0 ? '#0B303B' : '#ccc',
                  color: 'white',
                  fontWeight: '600',
                  transition: 'all 0.2s ease'
                }}
              >
                Create Cookbook ({selectedRecipesForCookbook.length} recipes)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cookbook Viewer Modal */}
      {selectedCookbook && (
        <div className="cookbook-viewer-overlay" onClick={handleCloseCookbookViewer}>
          <div className="cookbook-viewer" onClick={(e) => e.stopPropagation()}>
            <button className="cookbook-close-button" onClick={handleCloseCookbookViewer}>×</button>
            
            <div className="cookbook-pages-container">
              {currentPage === 0 ? (
                // Cover Page
                <div className="cookbook-cover-page">
                  <div className="cookbook-cover-content">
                    <div className={`cookbook-cover-design ${selectedCookbook.cover_image && selectedCookbook.cover_image.startsWith('custom:') ? 'custom' : selectedCookbook.cover_image}`}
                         style={selectedCookbook.cover_image && selectedCookbook.cover_image.startsWith('custom:') ? {
                           backgroundImage: `url(${selectedCookbook.cover_image.split('custom:')[1]})`,
                           backgroundSize: 'cover',
                           backgroundPosition: 'center'
                         } : {}}>
                    </div>
                    <div className="cookbook-cover-text">
                      <h1 className="cookbook-cover-title">{selectedCookbook.title}</h1>
                      <p className="cookbook-cover-description">{selectedCookbook.description}</p>
                      <div className="cookbook-cover-author">
                        <span className="cookbook-cover-author-name">
                          {userInfo.username || userInfo.firstName || 'User'}
                        </span>
                      </div>
                      <div className="cookbook-cover-meta">
                        <div className="cookbook-cover-recipes-count">{selectedCookbook.recipes_count} recipes</div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                // Recipe Pages (2-page spread)
                <div className="cookbook-recipe-spread">
                  {(() => {
                    const recipeIndex = (currentPage - 1) * 2;
                    const leftRecipe = selectedCookbook.recipes[recipeIndex];
                    const rightRecipe = selectedCookbook.recipes[recipeIndex + 1];
                    
                    return (
                      <>
                        {/* Left Page */}
                        <div className="cookbook-page cookbook-page-left">
                          {leftRecipe ? renderRecipeContent(leftRecipe) : (
                            <div className="cookbook-blank-page">This page is intentionally left blank</div>
                          )}
                        </div>
                        
                        {/* Right Page */}
                        <div className="cookbook-page cookbook-page-right">
                          {rightRecipe ? renderRecipeContent(rightRecipe) : (
                            <div className="cookbook-blank-page">This page is intentionally left blank</div>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
            
            {/* Navigation */}
            <div className="cookbook-navigation">
              <button 
                className="cookbook-nav-button cookbook-prev" 
                onClick={handlePrevPage}
                disabled={currentPage === 0}
              >
                ‹
              </button>
              <button 
                className="cookbook-nav-button cookbook-next" 
                onClick={handleNextPage}
                disabled={currentPage >= Math.ceil(selectedCookbook.recipes.length / 2)}
              >
                ›
              </button>
            </div>
            
            {/* Page Indicator */}
            <span className="cookbook-page-indicator">
              Page {currentPage + 1} of {1 + Math.ceil(selectedCookbook.recipes.length / 2)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default SavedRecipes;
